import { LicenceId } from '@ps/platform-message-framework/common/LicenceId';
import { Sites } from '@ps/platform-message-framework/common/Sites';
import { Country as Countries, FeatureFlag } from '@ps/web-core-enums';
import type { SelectOption } from '@ps/web-shared-components';
import { Translation, useIsMounted } from '@ps/web-shared-components';
import {
  GERMANY_BUILDING_NUMBER_ALLOWED_CHARACTERS,
  GERMANY_BUILDING_NUMBER_VALID_CHARACTERS,
  GERMANY_STREET_NAME_ALLOWED_CHARACTERS,
  GERMANY_STREET_NAME_VALID_CHARACTERS,
  NAME_AND_CITY_NOT_ALLOWED_SYMBOLS,
  maxLength,
  mustNotContainNumber,
  mustNotContainSymbols,
  noConsecutiveSpaces,
  noWhiteStartOrEnd,
  required,
  validateAllowedCharacters
} from '@ps/web-shared-validators';
import { format } from 'date-fns';
import { parsePhoneNumber } from 'libphonenumber-js';
import { formatAddress } from 'localized-address-format';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useField, useForm } from 'react-final-form';
import { useSelector } from 'react-redux';

import PhoneNumberInput from '@swf/components/PhoneNumberInput/PhoneNumberInput';
import useErrorHandling from '@swf/core/hooks/useErrorHandling';
import useFeature from '@swf/core/hooks/useFeature';
import useWizardContext from '@swf/core/hooks/useWizardContext';
import { select } from '@swf/core/store';
import type { WizardContext } from '@swf/core/types/Wizard';
import countries from '@swf/generated/countries.json';

import Country from '../../../../../components/Country/Country';
import InfoPrompt from '../../../../../components/InfoPrompt/InfoPrompt';
import ElectronicVerificationErrors from '../../../../../components/Prompts/ElectronicVerificationErrors/ElectronicVerificationErrors';
import { State } from '../../../../../types/frenchState';
import { VerificationErrorCode } from '../../../../../types/status';
import { getCountryStates } from '../../../../../utils/country-states/country-states';
import { getFrenchCitiesByState } from '../../../../../utils/placeOfBirth';
import Address from '../../../../Address/Address';
import Alert from '../../../../Alert/Alert';
import Checkbox from '../../../../Checkbox/Checkbox';
import City from '../../../../City/City';
import DOB from '../../../../DOB/DOB';
import DialingCode from '../../../../DialingCode/DialingCode';
import PostalCode from '../../../../PostalCode/PostalCode';
import { findState } from '../../../../State/State';
import TextInput from '../../../../TextInput/TextInput';
import CNP from '../../RMFCitizenship/components/CNP/CNP';
import RMFDropDown from '../../RMFDropDown/RMFDropDown';
import FamilyName from '../../RMFLegalName/components/FamilyName/FamilyName';
import GivenName from '../../RMFLegalName/components/GivenName/GivenName';
import MiddleName from '../../RMFLegalName/components/MiddleName/MiddleName';
import SummaryBox from '../SummaryBox/SummaryBox';

import {
  TXTACL_Please_Select,
  TXTCLI_Address,
  TXTCLI_City_of_Birth,
  TXTCLI_Country_canXt_be_changed,
  TXTCLI_Country_of_Birth,
  TXTCLI_Date_of_Birth,
  TXTCLI_Full_Name,
  TXTCLI_Place_of_Birth,
  TXTCLI_Please_select_your_department__ELL,
  TXTCLI_You_must_be_a_resident_of_the__ELL,
  TXTWCL_Birth_name,
  TXTWCL_CNP,
  TXTWCL_City_of_birth,
  TXTWCL_Country_cannot_be_changed,
  TXTWCL_Country_of_birth,
  TXTWCL_Department_of_birth,
  TXTWCL_House_Number_cannot_be_longer__ELL,
  TXTWCL_House_number_5,
  TXTWCL_House_number_cannot_be_0X_00X__ELL,
  TXTWCL_If_not_applicable_please_enter_ELL,
  TXTWCL_My_last_name_at_birth_is_diffe_ELL,
  TXTWCL_Please_enter_a_valid_cityX,
  TXTWCL_Please_select_your_city_of_bir_ELL,
  TXTWCL_Street_Name_cannot_be_longer_t_ELL,
  TXTWCL_Street_name_3,
  TXTWCL_The_name_you_have_entered_is_i_ELL,
  TXTWCL_To_verify_your_account_fasterX_ELL,
  TXTWCL_You_must_be_a_resident_of_Onta_ELL,
  TXTWCL_You_must_be_a_resident_of_this_ELL,
  TXTWCL__Phone_Number
} from './DataSummary.messages.json';
import { info, itemBox, itemsWrapper, phoneNumberBox, wrapper } from './DataSummary.scss';

const frenchStatesWithNoCities = [
  State.AntarcticTerritories,
  State.ClippertonIsland,
  State.FrenchPolynesia,
  State.NewCaledonia,
  State.SaintBarthelemy,
  State.SaintMartin,
  State.SaintPierre,
  State.WallisAndFutuna
];

const countryOfBirthField = 'countryOfBirth';
const stateOfBirthField = 'stateOfBirth';
const cityOfBirthField = 'cityOfBirth';
const birthNameField = 'birthName';

const MAX_LENGTH_HOUSE_NUMBER = 20;
const MAX_LENGTH_STREET_NAME = 150;

const notAllZeros = (value: string) => ({
  valid: !!value && !/^0+$/.test(value),
  message: TXTWCL_House_number_cannot_be_0X_00X__ELL
});

const DataSummary: React.FC = () => {
  const [editFullName, setEditFullName] = useState(false);
  const [editDateOfBirth, setEditDateOfBirth] = useState(false);
  const [editAddress, setEditAddress] = useState(false);
  const [editPhoneNumber, setEditPhoneNumber] = useState(false);
  const [editPlaceOfBirth, setEditPlaceOfBirth] = useState(false);
  const [editFiscalCode, setEditFiscalCode] = useState(false);
  const [isChecked, setIsChecked] = useState(false);
  const [showTooManyRetriesPrompt, setShowTooManyRetriesPrompt] = useState(false);
  const savedDob = useSelector(select.account.getDateOfBirth);
  const license = useSelector(select.account.getLicense);
  const placeOfBirth = useSelector(select.account.getPlaceOfBirth);
  const { removeError } = useErrorHandling();

  const {
    values: {
      firstName,
      lastName,
      birthName,
      dob,
      country,
      streetAddress,
      streetName,
      buildingNumber,
      city,
      zip,
      mobilePhoneNumber,
      fiscalCode
    },
    error
  }: WizardContext = useWizardContext();
  const siteId = useSelector(select.cms.getSiteId);
  const showCNP = siteId === Sites.ROM && country === Countries.Romania;
  const formattedNumber = mobilePhoneNumber ? parsePhoneNumber(mobilePhoneNumber, country) : null;
  const isDe = siteId === Sites.DE;
  const formattedStreetAddress = !isDe ? streetAddress : streetName + ' ' + buildingNumber;
  const failCountRef = useRef({ failCount: 0 });
  const [shouldShowEkycWarning, showEkycWarning] = useState<boolean>(false);
  const formattedAddress = formatAddress({
    postalCountry: country,
    locality: city,
    postalCode: zip,
    addressLines: [formattedStreetAddress]
  }).join('\n');

  const formattedDateOfBirth = format(!savedDob ? dob : savedDob, 'MM/dd/yyyy');

  const swcRetryDOB = useFeature(FeatureFlag.SwcRetryDOB);
  const swcRetryFiscalCode = useFeature(FeatureFlag.SwcRetryFiscalCode);
  const swcRetryPOB = useFeature(FeatureFlag.SwcRetryPOB);
  const swcRetryPhoneNumber = useFeature(FeatureFlag.SwcRetryPhoneNumber);

  const isFRLicense = license === LicenceId.FRANCE;
  const isSiteUS = useSelector(select.cms.isSiteUS);
  const isCAON = license === LicenceId.CAON;
  const showPrompt =
    [LicenceId.CAON, LicenceId.ROMANIA].includes(license) || isFRLicense || isSiteUS;
  const form = useForm();
  const [cityOptions, setCityOptions] = useState<SelectOption[]>([]);
  const isMounted = useIsMounted();
  const {
    input: { value: countryOfBirth }
  } = useField(countryOfBirthField, { initialValue: country });

  const {
    input: { value: stateOfBirth }
  } = useField(stateOfBirthField);

  const stateOptions =
    isFRLicense &&
    (getCountryStates(countryOfBirth) as { value: State; text: string }[]).filter(
      (state) => !frenchStatesWithNoCities.includes(state?.value)
    );
  const resetCityState = () => {
    form.change(cityOfBirthField);
    form.resetFieldState(cityOfBirthField);
  };

  const onChangeHandler = () => {
    setIsChecked(!isChecked);
    if (isChecked) {
      form.change(birthNameField);
    }
  };

  const getInfoText = () => {
    switch (true) {
      case isCAON:
        return TXTWCL_You_must_be_a_resident_of_Onta_ELL;
      case isSiteUS:
        return TXTCLI_You_must_be_a_resident_of_the__ELL;
      default:
        return TXTWCL_You_must_be_a_resident_of_this_ELL;
    }
  };

  useEffect(() => {
    if (
      error &&
      error?.globalErrors?.[0].code === VerificationErrorCode.ElectronicVerificationFailed
    ) {
      failCountRef.current.failCount++;
      showEkycWarning(failCountRef.current.failCount > 1);
    } else if (
      error &&
      error?.globalErrors?.[0].code ===
        VerificationErrorCode.ElectronicVerificationFailedTooManyRequests
    ) {
      setShowTooManyRetriesPrompt(true);
    } else {
      showEkycWarning(false);
      setEditFullName(false);
      setEditDateOfBirth(false);
      setEditAddress(false);
      setEditPhoneNumber(false);
      setEditPlaceOfBirth(false);
      setEditFiscalCode(false);
    }
  }, [error]);

  const setFrenchCities = useCallback(async () => {
    if (stateOptions) {
      const frenchCitiesByState = await getFrenchCitiesByState(
        findState(stateOfBirth, stateOptions)?.value ?? ''
      );
      isMounted() && setCityOptions(frenchCitiesByState);
    }
  }, [isMounted, stateOfBirth, stateOptions]);

  const onClose = () => {
    if (error) {
      removeError(error);
    }
  };

  useEffect(() => {
    setFrenchCities();
  }, [setFrenchCities]);

  return (
    <div className={itemsWrapper}>
      <div className={itemBox}>
        {shouldShowEkycWarning && (
          <Alert variant="info" className={info}>
            <Translation {...TXTWCL_To_verify_your_account_fasterX_ELL} />{' '}
          </Alert>
        )}
        {showTooManyRetriesPrompt && (
          <ElectronicVerificationErrors
            onClose={onClose}
            code={error?.globalErrors?.[0].code as string}
          />
        )}
        {editFullName ? (
          <>
            <GivenName autoFocus={false} />
            <MiddleName />
            <FamilyName />
            {isDe && (
              <>
                <Checkbox
                  checked={isChecked}
                  onChange={onChangeHandler}
                  label={<Translation {...TXTWCL_My_last_name_at_birth_is_diffe_ELL} />}
                />
                {isChecked && (
                  <TextInput
                    id="birthName"
                    label={TXTWCL_Birth_name}
                    value={birthName}
                    maxLength={50}
                    checkDisabled
                    validators={[
                      required(),
                      noWhiteStartOrEnd,
                      mustNotContainNumber(TXTWCL_The_name_you_have_entered_is_i_ELL, true),
                      mustNotContainSymbols(
                        NAME_AND_CITY_NOT_ALLOWED_SYMBOLS,
                        TXTWCL_The_name_you_have_entered_is_i_ELL
                      )
                    ]}
                  />
                )}
              </>
            )}
          </>
        ) : (
          <SummaryBox
            title={TXTCLI_Full_Name}
            details={`${firstName}  ${lastName}`}
            onClick={() => setEditFullName(true)}
          />
        )}
      </div>
      {swcRetryDOB && !savedDob && (
        <div className={itemBox}>
          {editDateOfBirth ? (
            <DOB />
          ) : (
            <SummaryBox
              title={TXTCLI_Date_of_Birth}
              details={formattedDateOfBirth}
              onClick={() => setEditDateOfBirth(true)}
            />
          )}
        </div>
      )}
      <div className={itemBox}>
        {editAddress ? (
          <>
            <Country
              country={country}
              fieldName="country"
              isRmfFlow
              labelContent={
                showPrompt ? (
                  // eslint-disable-next-line jsx-a11y/label-has-associated-control
                  <label>
                    <InfoPrompt
                      title={{
                        translation: isSiteUS
                          ? TXTCLI_Country_canXt_be_changed
                          : TXTWCL_Country_cannot_be_changed
                      }}
                    >
                      <p>
                        <Translation {...getInfoText()} />
                      </p>
                    </InfoPrompt>
                  </label>
                ) : null
              }
            />

            {isDe ? (
              <>
                <TextInput
                  id="streetName"
                  label={TXTWCL_Street_name_3}
                  value={streetName}
                  validators={[
                    required(TXTWCL_If_not_applicable_please_enter_ELL),
                    noConsecutiveSpaces,
                    noWhiteStartOrEnd,
                    maxLength(MAX_LENGTH_STREET_NAME, {
                      ...TXTWCL_Street_Name_cannot_be_longer_t_ELL,
                      values: { 0: MAX_LENGTH_STREET_NAME }
                    }),
                    validateAllowedCharacters(
                      GERMANY_STREET_NAME_ALLOWED_CHARACTERS,
                      GERMANY_STREET_NAME_VALID_CHARACTERS
                    )
                  ]}
                />
                <TextInput
                  id="buildingNumber"
                  label={TXTWCL_House_number_5}
                  value={buildingNumber}
                  validators={[
                    required(TXTWCL_If_not_applicable_please_enter_ELL),
                    notAllZeros,
                    noWhiteStartOrEnd,
                    noConsecutiveSpaces,
                    maxLength(MAX_LENGTH_HOUSE_NUMBER, {
                      ...TXTWCL_House_Number_cannot_be_longer__ELL,
                      values: { 0: MAX_LENGTH_HOUSE_NUMBER }
                    }),
                    validateAllowedCharacters(
                      GERMANY_BUILDING_NUMBER_ALLOWED_CHARACTERS,
                      GERMANY_BUILDING_NUMBER_VALID_CHARACTERS
                    )
                  ]}
                />
              </>
            ) : (
              <Address country={country} fieldName="streetAddress" value={formattedStreetAddress} />
            )}
            <City fieldName="city" />
            <PostalCode country={country} fieldName="zip" />
          </>
        ) : (
          <SummaryBox
            title={TXTCLI_Address}
            details={formattedAddress}
            onClick={() => setEditAddress(true)}
          />
        )}
      </div>
      {swcRetryPOB && isFRLicense && (
        <div className={itemBox}>
          {editPlaceOfBirth ? (
            <div className={wrapper}>
              <RMFDropDown
                id={countryOfBirthField}
                label={TXTCLI_Country_of_Birth}
                options={countries}
                onSelect={resetCityState}
              />

              {stateOptions && (
                <RMFDropDown
                  id={stateOfBirthField}
                  label={TXTWCL_Department_of_birth}
                  options={stateOptions}
                  onSelect={resetCityState}
                  validators={[required(TXTCLI_Please_select_your_department__ELL)]}
                  placeholder={<Translation {...TXTACL_Please_Select} />}
                />
              )}

              <RMFDropDown
                id={cityOfBirthField}
                label={TXTCLI_City_of_Birth}
                options={cityOptions}
                validators={[required(TXTWCL_Please_select_your_city_of_bir_ELL)]}
                placeholder={<Translation {...TXTACL_Please_Select} />}
                disabled={!cityOptions?.length}
                showPlaceholder
              />
            </div>
          ) : (
            <SummaryBox
              title={TXTCLI_Place_of_Birth}
              details={placeOfBirth}
              onClick={() => setEditPlaceOfBirth(true)}
            />
          )}
        </div>
      )}
      {swcRetryPOB && isDe && (
        <div className={itemBox}>
          {editPlaceOfBirth ? (
            <div className={wrapper}>
              <RMFDropDown
                id={countryOfBirthField}
                label={TXTWCL_Country_of_birth}
                options={countries}
                onSelect={resetCityState}
                placeholder={country}
              />
              <City
                fieldName={cityOfBirthField}
                requiredMessage={TXTWCL_Please_enter_a_valid_cityX}
                label={TXTWCL_City_of_birth}
                className={city}
              />
            </div>
          ) : (
            <SummaryBox
              title={TXTCLI_Place_of_Birth}
              details={placeOfBirth}
              onClick={() => setEditPlaceOfBirth(true)}
            />
          )}
        </div>
      )}
      {swcRetryPhoneNumber && formattedNumber && (
        <div className={itemBox}>
          {editPhoneNumber ? (
            <div className={phoneNumberBox}>
              <DialingCode fieldName="dialingCode" />
              <PhoneNumberInput phoneFormatCountry={country} fieldName="mobilePhoneNumber" />
            </div>
          ) : (
            <SummaryBox
              title={TXTWCL__Phone_Number}
              details={formattedNumber.number}
              onClick={() => setEditPhoneNumber(true)}
            />
          )}
        </div>
      )}
      {swcRetryFiscalCode && showCNP && (
        <div className={itemBox}>
          {editFiscalCode ? (
            <CNP />
          ) : (
            <SummaryBox
              title={TXTWCL_CNP}
              details={fiscalCode}
              onClick={() => setEditFiscalCode(true)}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default DataSummary;
